(() => {
  if (window.GodshotCartEnhancementsLoaded) return;
  window.GodshotCartEnhancementsLoaded = true;

  const rootUrl = () => window.Shopify?.routes?.root || '/';
  const unique = (values) =>
    Array.from(new Set(values.map((value) => String(value || '').trim()).filter(Boolean)));

  const fetchJson = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: { Accept: 'application/json', ...(options.headers || {}) },
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || data?.status) {
      throw new Error(data?.description || data?.message || 'Please try again.');
    }
    return data;
  };

  const getCart = () => fetchJson(`${rootUrl()}cart.js`);

  const writeDiscountCodes = (codes) =>
    fetchJson(`${rootUrl()}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ discount: unique(codes).join(',') }),
    });

  const setStatus = (element, message, isError = false) => {
    if (!element) return;
    element.textContent = message;
    element.classList.toggle('cart-enhancement-status--error', isError);
  };

  const applyDiscount = async (form) => {
    const input = form.elements.discount;
    const button = form.querySelector('button[type="submit"]');
    const status = form.querySelector('[data-cart-discount-status]');
    const requestedCode = String(input?.value || '').trim();
    if (!requestedCode) return;

    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    setStatus(status, 'Checking coupon…');

    let existingCodes = [];
    try {
      const before = await getCart();
      existingCodes = unique((before.discount_codes || []).map((discount) => discount?.code));
      const nextCodes = unique([...existingCodes, requestedCode]);
      if (nextCodes.length > 5) {
        throw new Error('A maximum of five discount codes can be used in one basket.');
      }

      const after = await writeDiscountCodes(nextCodes);
      const applied = (after.discount_codes || []).find(
        (discount) => String(discount?.code || '').toLowerCase() === requestedCode.toLowerCase()
      );

      if (!applied || applied.applicable === false) {
        await writeDiscountCodes(existingCodes);
        throw new Error('This coupon is not valid for the current basket.');
      }

      setStatus(status, 'Coupon applied. Updating your basket…');
      window.setTimeout(() => window.location.reload(), 350);
    } catch (error) {
      setStatus(status, error.message || 'The coupon could not be applied.', true);
      button.disabled = false;
      button.removeAttribute('aria-disabled');
    }
  };

  const money = (value) => {
    const amount = Number.parseFloat(value);
    const currency = window.Shopify?.currency?.active || 'GBP';
    if (!Number.isFinite(amount)) return `${value} ${currency}`;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (_error) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  const shippingQuery = (form) => {
    const parameters = new URLSearchParams();
    parameters.set('shipping_address[country]', form.elements.country.value);
    parameters.set('shipping_address[zip]', String(form.elements.zip.value || '').trim());
    if (form.elements.province?.value) {
      parameters.set('shipping_address[province]', form.elements.province.value);
    }
    return parameters;
  };

  const getShippingRates = async (parameters) => {
    await fetchJson(`${rootUrl()}cart/prepare_shipping_rates.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body: parameters.toString(),
    });

    for (let attempt = 0; attempt < 12; attempt += 1) {
      if (attempt > 0) await new Promise((resolve) => window.setTimeout(resolve, 450));
      const rates = await fetchJson(`${rootUrl()}cart/async_shipping_rates.json?${parameters.toString()}`);
      if (rates !== null) return rates.shipping_rates || [];
    }
    throw new Error('Shipping rates are taking longer than expected. Please try again.');
  };

  const renderShippingRates = (container, rates) => {
    container.classList.remove('cart-shipping-results--error');
    container.replaceChildren();

    if (!rates.length) {
      container.textContent = 'No delivery estimates are available for this destination yet.';
      return;
    }

    const intro = document.createElement('p');
    intro.className = 'cart-shipping-results__intro';
    intro.textContent = 'Estimated delivery options';
    container.appendChild(intro);

    const list = document.createElement('ul');
    list.className = 'cart-shipping-results__list';
    rates.forEach((rate) => {
      const item = document.createElement('li');
      item.className = 'cart-shipping-results__item';
      const name = document.createElement('span');
      name.textContent = rate.name || 'Delivery';
      const price = document.createElement('span');
      price.className = 'cart-shipping-results__price';
      price.textContent = money(rate.price);
      item.append(name, price);
      list.appendChild(item);
    });
    container.appendChild(list);

    const note = document.createElement('p');
    note.className = 'cart-shipping-results__note';
    note.textContent = 'These are estimates. Choose and confirm the final delivery method at checkout.';
    container.appendChild(note);
  };

  const calculateShipping = async (form) => {
    const button = form.querySelector('button[type="submit"]');
    const results = form.querySelector('[data-cart-shipping-results]');
    const postcode = String(form.elements.zip.value || '').trim();
    if (!postcode) return;

    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    results.classList.remove('cart-shipping-results--error');
    results.textContent = 'Calculating delivery estimates…';

    try {
      const rates = await getShippingRates(shippingQuery(form));
      renderShippingRates(results, rates);
    } catch (error) {
      results.textContent = error.message || 'Delivery estimates could not be calculated.';
      results.classList.add('cart-shipping-results--error');
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
    }
  };

  const initialiseShippingForm = (form) => {
    if (form.dataset.initialized === 'true') return;
    form.dataset.initialized = 'true';
    const country = form.elements.country;
    const province = form.elements.province;
    const container = form.querySelector('[data-cart-shipping-province-container]');
    if (country && province && container && window.Shopify?.CountryProvinceSelector) {
      new window.Shopify.CountryProvinceSelector(country.id, province.id, { hideElement: container.id });
    }
  };

  const initialise = (root = document) => {
    root.querySelectorAll('[data-cart-shipping-form]').forEach(initialiseShippingForm);
  };

  document.addEventListener('submit', (event) => {
    const discountForm = event.target.closest('[data-cart-discount-form]');
    if (discountForm) {
      event.preventDefault();
      applyDiscount(discountForm);
      return;
    }

    const shippingForm = event.target.closest('[data-cart-shipping-form]');
    if (shippingForm) {
      event.preventDefault();
      calculateShipping(shippingForm);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.matches?.('[data-cart-shipping-form]')) initialiseShippingForm(node);
        initialise(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
